import React from 'react';

interface DistributionHistogramProps {
    data: Record<string, number>;
    title: string;
    subtitle?: string;
    xAxisLabel?: string;
    barColor?: string;
}

export const DistributionHistogram: React.FC<DistributionHistogramProps> = ({
    data,
    title,
    subtitle,
    xAxisLabel,
    barColor = 'bg-primary/60',
}) => {
    const entries = Object.entries(data);

    if (entries.length === 0) {
        return (
            <div className="p-5 rounded-xl border-2 border-border/50 bg-card">
                <h3 className="text-lg font-semibold text-foreground mb-2">{title}</h3>
                <p className="text-sm text-muted-foreground">No data available</p>
            </div>
        );
    }

    const maxValue = Math.max(...entries.map(([, v]) => v));
    const total = entries.reduce((sum, [, v]) => sum + v, 0);

    return (
        <div className="p-5 rounded-xl border-2 border-border/50 bg-card">
            <h3 className="text-lg font-semibold text-foreground mb-1">{title}</h3>
            {subtitle && <p className="text-sm text-muted-foreground mb-4">{subtitle}</p>}
            <div className="flex items-end gap-1 sm:gap-2 mt-4" style={{ height: '180px' }}>
                {entries.map(([key, value]) => {
                    const heightPct = maxValue > 0 ? (value / maxValue) * 100 : 0;
                    const pct = total > 0 ? Math.round((value / total) * 100) : 0;
                    return (
                        <div key={key} className="flex-1 flex flex-col items-center gap-1 h-full justify-end">
                            <span className="text-xs text-muted-foreground">{value}</span>
                            <div
                                className={`w-full ${barColor} rounded-t-md transition-all duration-500 min-h-[4px]`}
                                style={{ height: `${Math.max(heightPct, 2)}%` }}
                                title={`${key}: ${value} (${pct}%)`}
                            />
                            <span className="text-xs text-muted-foreground text-center leading-tight mt-1">
                                {key}
                            </span>
                        </div>
                    );
                })}
            </div>
            {xAxisLabel && (
                <p className="text-xs text-muted-foreground text-center mt-2">{xAxisLabel}</p>
            )}
        </div>
    );
};
